import React from 'react';
import { AlertTriangle, Loader2, RefreshCw, ShieldAlert } from 'lucide-react';
import { fetchOperatorRecentLogins, fetchOperatorUsageStats } from '../services/supabase';
import type { OperatorAiUsageRow, OperatorRecentLogin, OperatorUsageStats } from '../types';

/* 運営者専用の「機能利用状況」。
 *
 * 設計の要点:
 *  - 認可は RPC の中で行う。この画面での出し分けは表示上の都合にすぎない。
 *    運営者以外が URL を直接開いても 42501 が返り、データは1バイトも来ない。
 *  - **機能名をハードコードしない。** ai_usage_log.function は文字列で後から
 *    種類が増える（ocr_import が実例）。RPC が返した行をそのまま並べるので、
 *    新機能が増えてもこのファイルは変更不要。表示名だけラベル表で解決し、
 *    無ければ生の値を出す（知らない機能を隠さない）。
 *  - データが少ない時期にゼロが並ぶのは許容する。件数が増えても同じ画面のまま
 *    使える形を優先している（作り直しを避ける方針）。
 *  - グラフは置かない。データ点が少なすぎて情報にならない。
 */

/** 機能名の表示ラベル。ここに無い機能は生の値をそのまま出す（隠さない）。 */
const FN_LABELS: Record<string, string> = {
  analyze_report: 'AI分析（報告）',
  transcribe_audio: '音声の文字起こし',
  ocr_import: '紙から取り込み（OCR）',
  why_analysis: '4Mなぜなぜ分析',
  poster_heat: 'ポスター 熱中症',
  poster_fall: 'ポスター 墜落・転落',
  poster_caught: 'ポスター はさまれ',
  poster_trip: 'ポスター 転倒',
  poster_general: 'ポスター 汎用',
};

const MODE_LABELS: Record<string, string> = {
  org: '本利用',
  demo: 'デモ',
  live: '実QR',
  sample: '見本QR',
};

function fnLabel(fn: string): string {
  return FN_LABELS[fn] ?? fn;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('ja-JP', { timeZone: 'Asia/Tokyo' });
  } catch {
    return '—';
  }
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' });
  } catch {
    return '—';
  }
}

const Tile: React.FC<{ label: string; value: React.ReactNode; sub?: string }> = ({ label, value, sub }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4">
    <p className="text-xs font-medium text-slate-500">{label}</p>
    <p className="text-2xl font-extrabold text-industrial-900 mt-1 tabular-nums">{value}</p>
    {sub && <p className="text-[11px] text-slate-500 mt-1">{sub}</p>}
  </div>
);

/** 機能×モードの利用表。RPC が返した行をそのまま並べる。 */
const UsageTable: React.FC<{ rows: OperatorAiUsageRow[]; emptyNote: string }> = ({ rows, emptyNote }) => {
  if (rows.length === 0) {
    return <p className="text-sm text-slate-500 py-6 text-center">{emptyNote}</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
            <th className="py-2 pr-4 font-medium">機能</th>
            <th className="py-2 pr-4 font-medium">区分</th>
            <th className="py-2 pr-3 font-medium text-right">24時間</th>
            <th className="py-2 pr-3 font-medium text-right">7日</th>
            <th className="py-2 pr-3 font-medium text-right">30日</th>
            <th className="py-2 pr-3 font-medium text-right">累計</th>
            <th className="py-2 pr-3 font-medium text-right">組織数</th>
            <th className="py-2 pr-3 font-medium text-right">トークン</th>
            <th className="py-2 pr-4 font-medium">初回 / 最終</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.fn}:${r.mode}`} className="border-b border-slate-100 last:border-0">
              <td className="py-2.5 pr-4 font-medium text-industrial-900">{fnLabel(r.fn)}</td>
              <td className="py-2.5 pr-4">
                <span
                  className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-bold ${
                    r.mode === 'demo' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-700'
                  }`}
                >
                  {MODE_LABELS[r.mode] ?? r.mode}
                </span>
              </td>
              <td className="py-2.5 pr-3 text-right tabular-nums">{r.d1}</td>
              <td className="py-2.5 pr-3 text-right tabular-nums">{r.d7}</td>
              <td className="py-2.5 pr-3 text-right tabular-nums">{r.d30}</td>
              <td className="py-2.5 pr-3 text-right tabular-nums font-bold">{r.total}</td>
              <td className="py-2.5 pr-3 text-right tabular-nums">{r.orgs}</td>
              <td className="py-2.5 pr-3 text-right tabular-nums text-slate-500">
                {r.tokens ? r.tokens.toLocaleString('ja-JP') : '—'}
              </td>
              <td className="py-2.5 pr-4 text-[11px] text-slate-500 whitespace-nowrap">
                {fmtDate(r.first_seen)} / {fmtDate(r.last_seen)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const RECENT_LOGIN_LIMIT = 100;

/* 最近ログインしたユーザーの一覧。
 *
 * ここだけは集計ではなく個人（メールアドレス）を出すので、取得も別のRPCに分けている。
 * 出るのは**管理者側のアカウント**であって、匿名で報告した作業員は含まれない。
 * 報告者の身元は cases にも残っていないので、この一覧から報告者は辿れない。 */
const RecentLogins: React.FC = () => {
  const [rows, setRows] = React.useState<OperatorRecentLogin[] | null>(null);
  const [message, setMessage] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let alive = true;
    void (async () => {
      const res = await fetchOperatorRecentLogins(RECENT_LOGIN_LIMIT);
      if (!alive) return;
      setRows(res.rows);
      setMessage(res.message);
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (loading) {
    return (
      <p className="text-sm text-slate-500 py-6 flex items-center justify-center gap-2">
        <Loader2 className="animate-spin h-4 w-4" /> 読み込み中...
      </p>
    );
  }
  if (!rows) {
    return <p className="text-sm text-slate-600 py-4">{message ?? '取得できませんでした。'}</p>;
  }
  if (rows.length === 0) {
    return <p className="text-sm text-slate-500 py-6 text-center">ログイン履歴がありません。</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
            <th className="py-2 pr-3 font-medium">#</th>
            <th className="py-2 pr-4 font-medium">メールアドレス</th>
            <th className="py-2 pr-4 font-medium">組織</th>
            <th className="py-2 pr-3 font-medium">プラン</th>
            <th className="py-2 pr-3 font-medium text-right">QR</th>
            <th className="py-2 pr-3 font-medium text-right">報告</th>
            <th className="py-2 pr-4 font-medium">最終ログイン</th>
            <th className="py-2 pr-4 font-medium">登録日</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.auth_id} className="border-b border-slate-100 last:border-0">
              <td className="py-2.5 pr-3 text-slate-400 tabular-nums">{i + 1}</td>
              <td className="py-2.5 pr-4 font-medium text-industrial-900 break-all">
                {r.email ?? '—'}
              </td>

              <td className="py-2.5 pr-4 text-slate-700">
                {r.organization_name ?? <span className="text-slate-400">—</span>}
              </td>
              <td className="py-2.5 pr-3">
                {r.organization_plan ? (
                  <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-bold bg-slate-100 text-slate-700">
                    {r.organization_plan}
                  </span>
                ) : (
                  <span className="text-slate-400">—</span>
                )}
              </td>
              <td className="py-2.5 pr-3 text-right tabular-nums text-slate-600">
                {r.organization_qr_points ?? 0}
              </td>
              <td className="py-2.5 pr-3 text-right tabular-nums text-slate-600">
                {r.organization_cases ?? 0}
              </td>
              <td className="py-2.5 pr-4 text-[11px] text-slate-600 whitespace-nowrap">
                {fmtDateTime(r.last_sign_in_at)}
              </td>
              <td className="py-2.5 pr-4 text-[11px] text-slate-500 whitespace-nowrap">
                {fmtDate(r.signed_up_at)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const OperatorUsageView: React.FC = () => {
  const [stats, setStats] = React.useState<OperatorUsageStats | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<{ message: string; forbidden: boolean } | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await fetchOperatorUsageStats();
    if (res.stats) {
      setStats(res.stats);
    } else {
      setError({ message: res.message ?? '利用状況を取得できませんでした。', forbidden: res.forbidden });
      setStats(null);
    }
    setLoading(false);
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin h-8 w-8 text-safety-600 mr-3" />
        <span className="text-slate-600">利用状況を読み込み中...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-lg mx-auto mt-10 bg-white rounded-xl border border-slate-200 p-8 text-center">
        <ShieldAlert className="h-10 w-10 text-slate-400 mx-auto mb-3" />
        <h2 className="text-lg font-bold text-industrial-900 mb-1">
          {error.forbidden ? '閲覧権限がありません' : '読み込めませんでした'}
        </h2>
        <p className="text-sm text-slate-600 mb-5">{error.message}</p>
        {!error.forbidden && (
          <button
            onClick={() => void load()}
            className="inline-flex items-center gap-2 bg-industrial-900 text-white px-5 py-2.5 rounded-lg text-sm font-bold hover:bg-slate-800 transition"
          >
            <RefreshCw size={16} /> 再読み込み
          </button>
        )}
      </div>
    );
  }

  if (!stats) return null;

  const t = stats.totals;
  const g = stats.demo_guard;
  const f = stats.funnel;
  const demoRatio = g.daily_limit > 0 ? Math.min(100, Math.round((g.today / g.daily_limit) * 100)) : 0;

  /* 「印刷」は migration 024 適用後に RPC が返す。未適用のうちは出さない
     （0 と表示すると「誰も刷っていない」と誤読されるため、無い場合は段自体を出さない）。 */
  const funnelSteps: Array<{ label: string; value: number }> = [
    { label: '登録', value: f.registered },
    { label: 'QR作成', value: f.created_qr },
    ...(typeof f.printed === 'number' ? [{ label: '印刷', value: f.printed }] : []),
    { label: 'スキャン', value: f.scanned },
    { label: '報告', value: f.reported },
    { label: '対応', value: f.resolved },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-industrial-900">機能利用状況</h1>
          <p className="text-sm text-slate-500 mt-1">
            運営者のみ閲覧できます。集計時点 {fmtDateTime(stats.generated_at)}
          </p>
        </div>
        <button
          onClick={() => void load()}
          className="inline-flex items-center gap-2 bg-white border border-slate-300 text-slate-700 px-4 py-2 rounded-lg text-sm font-bold hover:bg-slate-50 transition"
        >
          <RefreshCw size={15} /> 更新
        </button>
      </div>

      {/* 全体 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="組織" value={t.organizations} sub={`ユーザー ${t.users}人`} />
        <Tile label="QRポイント" value={t.qr_points} sub={`スキャン累計 ${t.qr_scans.toLocaleString('ja-JP')}`} />
        <Tile label="報告（累計）" value={t.cases} sub={`直近30日 ${t.cases_30d}件`} />
        <Tile
          label="未対応 / 対応済"
          value={`${t.cases_open} / ${t.cases_resolved}`}
          sub="status ベース"
        />
      </div>

      {/* 定着の段 */}
      <section className="bg-white rounded-xl border border-slate-200 p-5">
        <h2 className="font-bold text-industrial-900 mb-1">どこまで進んだ組織が何社あるか</h2>
        <p className="text-xs text-slate-500 mb-4">
          各段は「そこに到達した組織数」。到達した最も遠い段で数えるのではなく、条件を満たす組織を数えている（重複あり）。
          紙から取り込んだ報告はスキャンを経ずに「報告」に到達するため、厳密な一方向の流れではない。
        </p>
        <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${funnelSteps.length}, minmax(0, 1fr))` }}>
          {funnelSteps.map((s, i) => {
            const pct = f.registered > 0 ? Math.round((s.value / f.registered) * 100) : 0;
            return (
              <div key={s.label} className="text-center">
                <div className="text-[11px] text-slate-500 mb-1">{s.label}</div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg py-3">
                  <div className="text-xl font-extrabold text-industrial-900 tabular-nums">{s.value}</div>
                  {i > 0 && <div className="text-[10px] text-slate-500 mt-0.5">{pct}%</div>}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* デモ経路の監視 */}
      <section className="bg-white rounded-xl border border-slate-200 p-5">
        <h2 className="font-bold text-industrial-900 mb-1">デモ経路の監視</h2>
        <p className="text-xs text-slate-500 mb-4">
          デモ（登録不要）は認証なしで呼べる唯一のAI経路。実費が発生するため上限で頭打ちにしている。
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="col-span-2 md:col-span-1">
            <p className="text-xs font-medium text-slate-500 mb-1">
              本日 {g.today} / {g.daily_limit}
            </p>
            <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  demoRatio >= 80 ? 'bg-red-500' : demoRatio >= 50 ? 'bg-amber-500' : 'bg-emerald-500'
                }`}
                style={{ width: `${demoRatio}%` }}
              />
            </div>
            {demoRatio >= 80 && (
              <p className="text-[11px] text-red-700 font-bold mt-1.5 flex items-center gap-1">
                <AlertTriangle size={12} /> 上限に近づいています
              </p>
            )}
          </div>
          <Tile label="30日のデモ呼び出し" value={g.d30} />
          <Tile label="30日のユニークIP" value={g.unique_ips_30d} sub="SHA-256ハッシュの異なり数" />
        </div>
      </section>

      {/* 機能別の利用 */}
      <section className="bg-white rounded-xl border border-slate-200 p-5">
        <h2 className="font-bold text-industrial-900 mb-1">AI機能の利用</h2>
        <p className="text-xs text-slate-500 mb-4">
          計測は ai_usage_log（2026-06 以降）。それ以前の報告は含まれない。
          4Mなぜなぜ分析はこの表に載らない（記録を実装していないため）。
        </p>
        <UsageTable rows={stats.ai_usage} emptyNote="まだ利用がありません。" />
      </section>

      {/* ポスター（Phase 2 で RPC がフィールドを返したときだけ現れる） */}
      <section className="bg-white rounded-xl border border-slate-200 p-5">
        <h2 className="font-bold text-industrial-900 mb-1">掲示ポスターの利用</h2>
        {stats.poster_usage ? (
          <>
            <p className="text-xs text-slate-500 mb-4">
              <b className="text-slate-700">印刷ボタンを押した回数</b>で、印刷枚数ではありません
              （ダイアログでキャンセルしても1件入ります）。組織単位の集計のみで、誰が押したかは記録していません。
              「見本QR」は未ログインで刷られたもの＝現場に貼っても報告が届きません。
            </p>
            <UsageTable rows={stats.poster_usage} emptyNote="まだ利用がありません。" />
          </>
        ) : (
          <p className="text-sm text-slate-500 py-4">
            ポスターの計測は未導入です。紙面はブラウザ内で描画され印刷もクライアント側で行うため、
            計測を追加しない限りサーバに記録が残りません。
          </p>
        )}
      </section>

      {/* 最近ログインしたユーザー（個人情報を含むため取得は別RPC） */}
      <section className="bg-white rounded-xl border border-slate-200 p-5">
        <h2 className="font-bold text-industrial-900 mb-1">
          最近ログインしたユーザー（最大{RECENT_LOGIN_LIMIT}件）
        </h2>
        <p className="text-xs text-slate-500 mb-4">
          安全ポスト＋の組織を持つ利用者のみ。組織は管理画面に到達した時点で自動作成されるため、
          これが「実際に使い始めた人」の一覧になります。共有Supabaseの他アプリ（WhyTrace・know-howAI 等）の
          利用者は含まれません。匿名で報告した作業員も含まれません（報告者の身元は保存していないため辿れません）。
        </p>
        <RecentLogins />
      </section>
    </div>
  );
};
